/**
 * Curated list of IANA timezones for the admin event/venue timezone picker.
 *
 * Grouped by region for an <optgroup> dropdown. This is intentionally a
 * practical subset (not the full ~600 IANA zones) covering every populated
 * region — enough to set the correct local time for an event anywhere in the
 * world. Values are IANA identifiers consumed by Intl.DateTimeFormat /
 * date-fns-tz; labels are human-friendly.
 *
 * Offsets are deliberately NOT in the labels because they shift with DST.
 */
export interface TimezoneOption {
  value: string
  label: string
}

export interface TimezoneGroup {
  region: string
  zones: TimezoneOption[]
}

export const TIMEZONE_GROUPS: TimezoneGroup[] = [
  {
    region: 'United States',
    zones: [
      { value: 'America/New_York', label: 'Eastern Time (ET)' },
      { value: 'America/Chicago', label: 'Central Time (CT)' },
      { value: 'America/Denver', label: 'Mountain Time (MT)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
      { value: 'America/Phoenix', label: 'Arizona Time (MST, no DST)' },
      { value: 'America/Anchorage', label: 'Alaska Time (AKST)' },
      { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
    ],
  },
  {
    region: 'Canada',
    zones: [
      { value: 'America/Toronto', label: 'Eastern - Toronto' },
      { value: 'America/Winnipeg', label: 'Central - Winnipeg' },
      { value: 'America/Edmonton', label: 'Mountain - Edmonton' },
      { value: 'America/Vancouver', label: 'Pacific - Vancouver' },
      { value: 'America/Halifax', label: 'Atlantic - Halifax' },
      { value: 'America/St_Johns', label: 'Newfoundland - St. John’s' },
    ],
  },
  {
    region: 'Latin America',
    zones: [
      { value: 'America/Mexico_City', label: 'Mexico City' },
      { value: 'America/Bogota', label: 'Bogotá' },
      { value: 'America/Lima', label: 'Lima' },
      { value: 'America/Sao_Paulo', label: 'São Paulo' },
      { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires' },
      { value: 'America/Santiago', label: 'Santiago' },
    ],
  },
  {
    region: 'United Kingdom & Ireland',
    zones: [
      { value: 'Europe/London', label: 'London (GMT/BST)' },
      { value: 'Europe/Dublin', label: 'Dublin' },
    ],
  },
  {
    region: 'Europe',
    zones: [
      { value: 'Europe/Lisbon', label: 'Lisbon' },
      { value: 'Europe/Madrid', label: 'Madrid' },
      { value: 'Europe/Paris', label: 'Paris' },
      { value: 'Europe/Amsterdam', label: 'Amsterdam' },
      { value: 'Europe/Berlin', label: 'Berlin' },
      { value: 'Europe/Rome', label: 'Rome' },
      { value: 'Europe/Stockholm', label: 'Stockholm' },
      { value: 'Europe/Athens', label: 'Athens' },
      { value: 'Europe/Moscow', label: 'Moscow' },
    ],
  },
  {
    region: 'Africa & Middle East',
    zones: [
      { value: 'Africa/Lagos', label: 'Lagos' },
      { value: 'Africa/Johannesburg', label: 'Johannesburg' },
      { value: 'Africa/Cairo', label: 'Cairo' },
      { value: 'Africa/Nairobi', label: 'Nairobi' },
      { value: 'Asia/Jerusalem', label: 'Jerusalem' },
      { value: 'Asia/Dubai', label: 'Dubai' },
    ],
  },
  {
    region: 'Asia',
    zones: [
      { value: 'Asia/Karachi', label: 'Karachi' },
      { value: 'Asia/Kolkata', label: 'India (Kolkata/Mumbai)' },
      { value: 'Asia/Dhaka', label: 'Dhaka' },
      { value: 'Asia/Bangkok', label: 'Bangkok' },
      { value: 'Asia/Singapore', label: 'Singapore' },
      { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
      { value: 'Asia/Shanghai', label: 'China (Shanghai/Beijing)' },
      { value: 'Asia/Tokyo', label: 'Tokyo' },
      { value: 'Asia/Seoul', label: 'Seoul' },
      { value: 'Asia/Manila', label: 'Manila' },
    ],
  },
  {
    region: 'Australia & New Zealand',
    zones: [
      { value: 'Australia/Perth', label: 'Perth (AWST)' },
      { value: 'Australia/Adelaide', label: 'Adelaide (ACST)' },
      { value: 'Australia/Darwin', label: 'Darwin (ACST, no DST)' },
      { value: 'Australia/Brisbane', label: 'Brisbane (AEST, no DST)' },
      { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
      { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
      { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
      { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
    ],
  },
]

/** Flat list of every option, for lookups/validation. */
export const TIMEZONE_OPTIONS: TimezoneOption[] = TIMEZONE_GROUPS.flatMap(g => g.zones)

/**
 * Human timezone abbreviation for a given instant — e.g. "AEST", "AEDT",
 * "EDT", "PST", "GMT", "BST".
 *
 * Why not just use Intl's `timeZoneName: 'short'`? Because it's only reliable
 * for US zones. For most other zones (notably Australia/Sydney) many JS
 * runtimes return a raw offset like "GMT+10" instead of "AEST". The *long*
 * localized name ("Australian Eastern Standard Time") IS reliable across
 * runtimes, so we derive the abbreviation from its capitalised initials and
 * only fall back to the short/offset form for zones that genuinely have no
 * named abbreviation.
 */
const ABBREVIATION_OVERRIDES: Record<string, { standard: string; daylight: string }> = {
  // Western European
  'Europe/Lisbon': { standard: 'WET', daylight: 'WEST' },
  // Central European
  'Europe/Madrid': { standard: 'CET', daylight: 'CEST' },
  'Europe/Paris': { standard: 'CET', daylight: 'CEST' },
  'Europe/Amsterdam': { standard: 'CET', daylight: 'CEST' },
  'Europe/Berlin': { standard: 'CET', daylight: 'CEST' },
  'Europe/Rome': { standard: 'CET', daylight: 'CEST' },
  'Europe/Stockholm': { standard: 'CET', daylight: 'CEST' },
  // Eastern European
  'Europe/Athens': { standard: 'EET', daylight: 'EEST' },
  // Moscow (no DST since 2014)
  'Europe/Moscow': { standard: 'MSK', daylight: 'MSK' },
}

export function tzAbbreviation(date: Date, timeZone: string): string {
  if (!timeZone) return ''

  const namePart = (style: 'short' | 'long'): string => {
    try {
      return (
        new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: style })
          .formatToParts(date)
          .find(p => p.type === 'timeZoneName')?.value || ''
      )
    } catch {
      return ''
    }
  }

  const long = namePart('long')

  // UTC long name is "Coordinated Universal Time" — initials would be wrong.
  if (/universal/i.test(long)) return 'UTC'

  // Zones whose conventional abbreviation drops a word the long name keeps
  // (e.g. "Central European Standard Time" is conventionally "CET", not the
  // initials "CEST"). DST is detected from the long name (Summer/Daylight).
  const override = ABBREVIATION_OVERRIDES[timeZone]
  if (override) {
    const isDst = /summer|daylight/i.test(long)
    return isDst ? override.daylight : override.standard
  }

  // Offset-only long name (e.g. "GMT+05:30") — nothing meaningful to
  // abbreviate, so keep whatever short form the runtime gives.
  if (!long || /\d/.test(long) || /^(GMT|UTC)\b/i.test(long)) {
    return namePart('short')
  }

  // "Australian Eastern Standard Time" -> "AEST"; "Eastern Daylight Time" -> "EDT".
  const initials = long
    .split(/\s+/)
    .map(word => word[0])
    .filter((c): c is string => !!c && /[A-Z]/.test(c) && c === c.toUpperCase())
    .join('')

  return initials.length >= 2 ? initials : namePart('short') || long
}
